/**
 * Reine Logik der oeffentlichen Freigabeseite #/gbu-freigabe/<token>
 * (docs/sifa-gbu-plan.md §8, Bauvertrag §14.4 Abschnitt G).
 *
 * Kein React, kein Supabase: Zustaende aus den RPC-Antworten, Texte je
 * Rolle, Eingabepruefung (dieselben Grenzen wie die RPC
 * `sifa_gbu_freigabe_unterschreiben`), die kompakte Gefaehrdungstabelle und
 * Datumsformate. Die Komponenten (GbuFreigabeSeite.tsx) bleiben duenn — es
 * gibt kein jsdom im Test.
 *
 * Der Link gilt fuer GENAU eine eingefrorene Fassung, nur einmal, 14 Tage
 * lang. Was die Seite zeigt, kommt vollstaendig aus `inhalt` (Namen aus
 * Kataster und Katalogen sind dort schon aufgeloest, gbuTypes.ts).
 */

import type {
  FreigabePublicErgebnis,
  FreigabeUnterschriftErgebnis,
  GbuAnlass,
  GbuFassungInhalt,
  GbuUnterschrift,
  MassnahmeArt,
  RisikoStufe,
  UnterschriftRolle,
} from '../../../../services/sifa/gbuTypes';

// ─── Grenzen (wie die RPC) ───────────────────────────────────────────────────

/** Name 1–120 Zeichen (Check in sifa_gbu_freigabe_unterschreiben). */
export const NAME_MAX = 120;
/** Unterschrift als `data:image/…`, hoechstens 150 000 Zeichen (wie sifa_submit_signature). */
export const BILD_MAX_ZEICHEN = 150_000;
/** Breite der Unterschrift im PDF — wie auf der Teilnehmerseite der Unterweisung. */
export const UNTERSCHRIFT_MAX_BREITE = 480;

// ─── Route ───────────────────────────────────────────────────────────────────

const ROUTE = /^#\/gbu-freigabe\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/;

/** Token aus `#/gbu-freigabe/<token>` (index.tsx); null = keine Freigabeseite. */
export const gbuFreigabeToken = (hash: string | null | undefined): string | null =>
  ROUTE.exec(hash ?? '')?.[1] ?? null;

/**
 * Vollstaendige Adresse des Freigabe-Links fuer Mail und QR (Muster
 * `freimeldungLinkUrl`): die App liegt nicht zwingend an der Wurzel, der
 * Hash-Router liegt neben dem Pfad.
 */
export const gbuFreigabeUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/gbu-freigabe/${encodeURIComponent(token)}`;

// ─── Zustaende ───────────────────────────────────────────────────────────────

/**
 * Was die Seite gerade zeigt.
 *  - `unbekannt`  Token gibt es nicht (oder nicht mehr).
 *  - `abgelaufen` Link aelter als seine Gueltigkeit.
 *  - `schema`     RPC fehlt (Migration Nr. 168 nicht gelaufen).
 *  - `netz`       keine Verbindung beim Laden — „Erneut versuchen".
 *  - `bereits`    dieser Link wurde schon benutzt.
 *  - `formular`   Kurzfassung, Name, Haekchen, Unterschrift, Freigeben.
 *  - `erfolg`     soeben unterschrieben.
 */
export type FreigabeZustand =
  | 'laedt'
  | 'unbekannt'
  | 'abgelaufen'
  | 'schema'
  | 'netz'
  | 'bereits'
  | 'formular'
  | 'erfolg';

const istObjekt = (v: unknown): boolean => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Traegt der Inhalt, was die Seite liest? Der Dienst reicht das jsonb der
 * Fassung ungeprueft weiter; eine fremde `schema`-Nummer oder ein kaputter
 * Datensatz darf die Seite nicht abstuerzen lassen.
 */
export const inhaltLesbar = (inhalt: unknown): inhalt is GbuFassungInhalt => {
  if (!istObjekt(inhalt)) return false;
  const i = inhalt as Record<string, unknown>;
  return i.schema === 1
    && Array.isArray(i.gefaehrdungen)
    && istObjekt(i.kunde) && istObjekt(i.buero) && istObjekt(i.bereich) && istObjekt(i.rollen);
};

/**
 * Zustand nach dem Laden. Ein unlesbarer Inhalt gilt wie im Dienst als
 * unbekannter Link: lieber eine Absage als ein Blatt zum Unterschreiben, auf
 * dem etwas fehlt.
 */
export const zustandNachLaden = (e: FreigabePublicErgebnis): FreigabeZustand => {
  if (e.ok === false) return e.fehler;
  if (!inhaltLesbar(e.inhalt)) return 'unbekannt';
  return e.bereits ? 'bereits' : 'formular';
};

/**
 * Zustand nach „Freigeben". Ein Eingabe-, Schema- oder Netzfehler laesst das
 * Formular stehen (mit Fehlertext, erneut versuchbar); ein unbekannter oder
 * abgelaufener Link wechselt auf die Endansicht, weil ein zweiter Versuch
 * nichts aendert. `bereits` (der Link wurde inzwischen benutzt, etwa in einem
 * zweiten Tab) sagt das ehrlich, statt einen Erfolg vorzutaeuschen.
 */
export const ergebnisNachUnterschrift = (
  e: FreigabeUnterschriftErgebnis,
): { zustand: FreigabeZustand; fehler: string | null } => {
  if (e.ok === true) return { zustand: e.bereits ? 'bereits' : 'erfolg', fehler: null };
  switch (e.fehler) {
    case 'unbekannt':
    case 'abgelaufen':
      return { zustand: e.fehler, fehler: null };
    case 'eingabe':
      return { zustand: 'formular', fehler: e.text?.trim() || 'Bitte Namen und Unterschrift prüfen und erneut freigeben.' };
    case 'schema':
      return { zustand: 'formular', fehler: 'Die Freigabe ist gerade nicht möglich. Bitte später erneut versuchen oder das Büro ansprechen.' };
    case 'netz':
    default:
      return { zustand: 'formular', fehler: 'Keine Verbindung. Bitte die Internetverbindung prüfen und es erneut versuchen.' };
  }
};

/**
 * Nach einer angenommenen Unterschrift (auch `bereits`) laedt die Seite die
 * Fassung neu und verwirft ein schon erzeugtes PDF — sonst zeigte das
 * naechste PDF das eigene Feld noch als offen.
 */
export const mussNachUnterschriftNachladen = (z: FreigabeZustand): boolean => z === 'erfolg' || z === 'bereits';

/**
 * Was das Nachladen nach der Unterschrift uebernimmt: nur die Daten, nie den
 * Zustand. Die RPC meldet den Link jetzt als benutzt (`bereits`); die Seite
 * soll aber „Freigegeben" zeigen, nicht „Bereits freigegeben". Ein Fehler
 * oder ein unlesbarer Inhalt laesst den bisherigen Stand stehen (null).
 */
export const nachgeladeneDaten = (
  e: FreigabePublicErgebnis,
): Extract<FreigabePublicErgebnis, { ok: true }> | null =>
  e.ok === true && inhaltLesbar(e.inhalt) ? e : null;

/** Endansichten ohne Formular: Titel und Erklaerung. */
export const ENDANSICHT_TEXTE: Record<'unbekannt' | 'abgelaufen' | 'schema' | 'netz', { titel: string; text: string }> = {
  unbekannt: {
    titel: 'Dieser Link ist nicht gültig',
    text: 'Den Freigabe-Link gibt es nicht oder nicht mehr. Bitte wenden Sie sich an das Büro, von dem Sie den Link bekommen haben.',
  },
  abgelaufen: {
    titel: 'Dieser Link ist abgelaufen',
    text: 'Ein Freigabe-Link gilt 14 Tage. Bitte fordern Sie beim Büro einen neuen Link an.',
  },
  schema: {
    titel: 'Die Freigabe ist noch nicht eingerichtet',
    text: 'Die Freigabe per Link steht gerade nicht zur Verfügung. Bitte versuchen Sie es später erneut oder wenden Sie sich an das Büro.',
  },
  netz: {
    titel: 'Die Gefährdungsbeurteilung konnte nicht geladen werden',
    text: 'Keine Verbindung zum Server. Bitte prüfen Sie die Internetverbindung und versuchen Sie es erneut.',
  },
};

// ─── Texte je Rolle ──────────────────────────────────────────────────────────

export const ROLLE_LABEL: Record<UnterschriftRolle, string> = {
  erstellt: 'Erstellt',
  geprueft: 'Geprüft',
  freigegeben: 'Freigegeben',
};

export interface RollenTexte {
  titel: string;
  einleitung: string;
  /** Ueberschrift des Abschnitts mit Name, Haekchen und Unterschrift. */
  abschnitt: string;
  haekchen: string;
  knopf: string;
  erfolgTitel: string;
  erfolgText: string;
  bereitsTitel: string;
  bereitsText: string;
}

/**
 * Texte der Seite. Der Freigabe-Link geht in der Regel an den Unternehmer
 * (Rolle `freigegeben`); die Rollen `erstellt` und `geprueft` lassen sich
 * ebenfalls per Link unterschreiben (Check in sifa_gbu_freigabe_links).
 */
export const rollenTexte = (rolle: UnterschriftRolle): RollenTexte => {
  const bereitsTitel = 'Bereits unterschrieben';
  const bereitsText = 'Mit diesem Link wurde schon unterschrieben. Ein Link gilt nur einmal — es ist nichts weiter zu tun.';
  switch (rolle) {
    case 'geprueft':
      return {
        titel: 'Gefährdungsbeurteilung prüfen',
        einleitung: 'Bitte lesen Sie die Gefährdungsbeurteilung, tragen Sie Ihren Namen ein und bestätigen Sie die Prüfung mit Ihrer Unterschrift.',
        abschnitt: 'Prüfung',
        haekchen: 'Ich habe die Gefährdungsbeurteilung gelesen und geprüft',
        knopf: 'Prüfung bestätigen',
        erfolgTitel: 'Prüfung bestätigt',
        erfolgText: 'Vielen Dank. Ihre Unterschrift ist bei der Gefährdungsbeurteilung hinterlegt.',
        bereitsTitel,
        bereitsText,
      };
    case 'erstellt':
      return {
        titel: 'Gefährdungsbeurteilung unterschreiben',
        einleitung: 'Bitte lesen Sie die Gefährdungsbeurteilung, tragen Sie Ihren Namen ein und unterschreiben Sie als Ersteller.',
        abschnitt: 'Unterschrift',
        haekchen: 'Ich habe die Gefährdungsbeurteilung gelesen und bestätige sie als Ersteller',
        knopf: 'Unterschreiben',
        erfolgTitel: 'Unterschrieben',
        erfolgText: 'Vielen Dank. Ihre Unterschrift ist bei der Gefährdungsbeurteilung hinterlegt.',
        bereitsTitel,
        bereitsText,
      };
    case 'freigegeben':
    default:
      return {
        titel: 'Gefährdungsbeurteilung freigeben',
        einleitung: 'Bitte lesen Sie die Gefährdungsbeurteilung, tragen Sie Ihren Namen ein und geben Sie sie mit Ihrer Unterschrift frei.',
        abschnitt: 'Freigabe',
        haekchen: 'Ich habe die Gefährdungsbeurteilung gelesen und gebe sie frei',
        knopf: 'Freigeben',
        erfolgTitel: 'Freigegeben',
        erfolgText: 'Vielen Dank. Die Gefährdungsbeurteilung ist freigegeben; das Büro erhält die Freigabe automatisch.',
        bereitsTitel: 'Bereits freigegeben',
        bereitsText: 'Mit diesem Link wurde die Gefährdungsbeurteilung schon freigegeben. Ein Link gilt nur einmal — es ist nichts weiter zu tun.',
      };
  }
};

export const ANLASS_LABEL: Record<GbuAnlass, string> = {
  ersterstellung: 'Ersterstellung',
  aenderung: 'Änderung',
  ereignis: 'Nach einem Ereignis',
  regelpruefung: 'Regelmäßige Überprüfung',
};

export const MASSNAHME_ART_LABEL: Record<MassnahmeArt, string> = {
  S: 'Substitution',
  T: 'Technisch',
  O: 'Organisatorisch',
  P: 'Persönlich',
};

// ─── Eingabe ─────────────────────────────────────────────────────────────────

/** Name ohne Rand und doppelte Leerzeichen — so geht er an die RPC. */
export const bereinigeName = (roh: string | null | undefined): string => (roh ?? '').replace(/\s+/g, ' ').trim();

/** Nimmt die RPC dieses Bild an? */
export const bildGueltig = (bild: string | null | undefined): boolean =>
  typeof bild === 'string' && bild.startsWith('data:image/') && bild.length <= BILD_MAX_ZEICHEN;

export interface FreigabeEingabe {
  name: string;
  gelesen: boolean;
  bild: string | null;
}

/**
 * Was vor „Freigeben" noch fehlt, in der Reihenfolge der Seite. Leer = es
 * darf freigegeben werden. Ein zu grosses Bild ist kein „fehlt", sondern
 * ein Fehler (`eingabeFehler`).
 */
export const fehlendeAngaben = (e: FreigabeEingabe): string[] => {
  const fehlt: string[] = [];
  if (!bereinigeName(e.name)) fehlt.push('Ihr Name');
  if (!e.gelesen) fehlt.push('das Häkchen');
  if (!e.bild) fehlt.push('Ihre Unterschrift');
  return fehlt;
};

/** Harte Fehler der Eingabe (Laenge, Bildformat); null = in Ordnung. */
export const eingabeFehler = (e: FreigabeEingabe): string | null => {
  if (bereinigeName(e.name).length > NAME_MAX) return `Der Name darf höchstens ${NAME_MAX} Zeichen lang sein.`;
  if (e.bild && !bildGueltig(e.bild)) return 'Die Unterschrift ließ sich nicht übernehmen. Bitte löschen und erneut unterschreiben.';
  return null;
};

export const kannFreigeben = (e: FreigabeEingabe): boolean =>
  fehlendeAngaben(e).length === 0 && eingabeFehler(e) === null;

/** „Es fehlen noch: Ihr Name und das Häkchen." — null, wenn nichts fehlt. */
export const fehltHinweis = (fehlt: readonly string[]): string | null => {
  if (fehlt.length === 0) return null;
  const liste = fehlt.length === 1 ? fehlt[0] : `${fehlt.slice(0, -1).join(', ')} und ${fehlt[fehlt.length - 1]}`;
  return `Es fehlen noch: ${liste}.`;
};

// ─── Bisherige Unterschriften ────────────────────────────────────────────────

const ROLLEN: readonly UnterschriftRolle[] = ['erstellt', 'geprueft', 'freigegeben'];
const WEGE: readonly GbuUnterschrift['weg'][] = ['geraet', 'link', 'papier'];

/**
 * Die bisherigen Unterschriften der Fassung. Die RPC
 * `sifa_gbu_freigabe_public` liefert sie unter dem EIGENEN Schluessel
 * `unterschriften` (nicht in `inhalt`, §14.2). Gelesen wird defensiv: fehlt
 * der Schluessel oder ist ein Eintrag unvollstaendig, faellt er weg — das
 * PDF zeigt dann ein leeres Feld statt eines kaputten.
 */
export const bisherigeUnterschriften = (ergebnis: unknown): GbuUnterschrift[] => {
  const roh = (ergebnis as { unterschriften?: unknown } | null)?.unterschriften;
  if (!Array.isArray(roh)) return [];
  return roh.flatMap((u): GbuUnterschrift[] => {
    if (!u || typeof u !== 'object') return [];
    const r = u as Record<string, unknown>;
    if (!ROLLEN.includes(r.rolle as UnterschriftRolle)) return [];
    if (typeof r.name !== 'string' || !r.name.trim() || typeof r.datum !== 'string') return [];
    return [{
      rolle: r.rolle as UnterschriftRolle,
      name: r.name,
      funktion: typeof r.funktion === 'string' ? r.funktion : null,
      datum: r.datum,
      bild: typeof r.bild === 'string' && r.bild.startsWith('data:image/') ? r.bild : null,
      weg: WEGE.includes(r.weg as GbuUnterschrift['weg']) ? (r.weg as GbuUnterschrift['weg']) : 'link',
    }];
  });
};

// ─── Kurzfassung ─────────────────────────────────────────────────────────────

export interface KurzMassnahme {
  id: string;
  art: MassnahmeArt;
  artLabel: string;
  text: string;
}

export interface KurzZeile {
  id: string;
  nr: number;
  faktorLabel: string;
  gefaehrdung: string;
  folgen: string | null;
  rVor: number;
  rNach: number;
  /** R nachher ≥ 6 — hervorheben, sperrt aber nichts (Entscheid O4). */
  hochNachher: boolean;
  massnahmen: KurzMassnahme[];
  /** Arbeitsmittel und Stoffe in einer Zeile, leer = keine. */
  bezug: string;
}

/** Massnahmentext, bei PSA mit der Bezeichnung aus dem Katalog, wenn sie nicht schon drinsteht. */
const massnahmeText = (text: string, psaLabel: string | null): string => {
  const t = text.trim();
  if (!psaLabel) return t;
  if (!t) return psaLabel;
  return t.toLowerCase().includes(psaLabel.toLowerCase()) ? t : `${t} (${psaLabel})`;
};

/** Die Gefaehrdungstabelle kompakt: eine Zeile je Gefaehrdung, nach Nr. */
export const kurzZeilen = (inhalt: GbuFassungInhalt): KurzZeile[] =>
  [...inhalt.gefaehrdungen]
    .sort((a, b) => a.nr - b.nr)
    .map((g) => ({
      id: g.id,
      nr: g.nr,
      faktorLabel: g.faktorLabel,
      gefaehrdung: g.gefaehrdung,
      folgen: g.folgen?.trim() || null,
      rVor: g.rVor,
      rNach: g.rNach,
      hochNachher: g.rNach >= 6,
      massnahmen: (g.massnahmen ?? []).map((m) => ({
        id: m.id,
        art: m.art,
        artLabel: MASSNAHME_ART_LABEL[m.art] ?? m.art,
        text: massnahmeText(m.text, m.psaLabel),
      })),
      bezug: [...(g.arbeitsmittel ?? []), ...(g.stoffe ?? [])].filter((s) => typeof s === 'string' && s.trim()).join(', '),
    }));

/**
 * Farbe und Wort je Risikostufe. Die Stufe selbst rechnet
 * gbuLogic.risikoStufe (eine Stelle fuer die 3×3-Regel); hier steht nur,
 * wie die Seite sie zeigt.
 */
export const RISIKO_ANZEIGE: Record<RisikoStufe, { label: string; klasse: string }> = {
  gering: { label: 'gering', klasse: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  mittel: { label: 'mittel', klasse: 'bg-amber-100 text-amber-800 border-amber-200' },
  hoch: { label: 'hoch', klasse: 'bg-red-100 text-red-800 border-red-200' },
};

/** Zahl der Gefaehrdungen mit hohem Restrisiko (R nachher ≥ 6). */
export const anzahlHochNachher = (zeilen: readonly KurzZeile[]): number => zeilen.filter((z) => z.hochNachher).length;

/** Bereich in einer Zeile: „Einschalen · Rohbau · Baustelle Nord". */
export const bereichZeile = (b: GbuFassungInhalt['bereich']): string =>
  [b.bezeichnung, b.bereich, b.ort].map((s) => (s ?? '').trim()).filter(Boolean).join(' · ');

/** „GBU Nr. 3 · Fassung 2" */
export const kennungZeile = (inhalt: Pick<GbuFassungInhalt, 'nummer' | 'fassung'>): string =>
  `GBU Nr. ${inhalt.nummer} · Fassung ${inhalt.fassung}`;

/** Dateiname des PDF — dieselbe Kennung wie die Ablage (`fassung_ref` GBU-<nr>-F<fassung>). */
export const pdfDateiname = (inhalt: Pick<GbuFassungInhalt, 'nummer' | 'fassung'>): string =>
  `GBU-${inhalt.nummer}-F${inhalt.fassung}.pdf`;

// ─── Datum ───────────────────────────────────────────────────────────────────

/** yyyy-mm-dd → TT.MM.JJJJ als Zeichenkette (kein `new Date`: Zeitzonenfehler). */
export const formatDatum = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const BERLIN_DATUM = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric',
});

/**
 * Zeitstempel (timestamptz) → Berliner Kalendertag TT.MM.JJJJ. Ein Link,
 * der um 23:30 Uhr Berliner Zeit ablaeuft, steht in UTC schon am Vortag; das
 * Abschneiden der Zeichenkette zeigte dann den falschen Tag. Ein reines
 * Datum geht ueber `formatDatum`.
 */
export const formatBerlinerTag = (zeitpunkt: string | null | undefined): string => {
  if (!zeitpunkt) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(zeitpunkt)) return formatDatum(zeitpunkt);
  const ms = Date.parse(zeitpunkt);
  return Number.isNaN(ms) ? zeitpunkt : BERLIN_DATUM.format(ms);
};
